import React, { memo, useContext, useState } from 'react';
import { MemberOrderResponse } from '../../data_types/member-order';
import OrderComponent, { OrderComponentProps } from './OrderComponent';
import createPayload from '../../utils/order-create-payload';
import sendPayload from '../../utils/order-send-payload';
import { getDefaultSubscription } from '../../utils/get-default-subscription';
import { generateRenewSchema } from '../../utils/generate-renew-schema';
import { generateRenewFormOptions } from '../../utils/generate-renew-form-options';
import { FormProvider, useForm } from 'react-hook-form';
import { chargesSupplementalDues } from '../../utils/charges-supplemental-dues';
import OrderRedirectToCheckout from './RedirectToCheckout/RedirectToCheckout';
import { ModalContext } from '../../contexts/ModalContext';
import { BackdropContext } from '../../contexts/BackdropContext';
import { DEFAULT_MODAL_CONTENT, INVALID_SESSION_MODAL_CONTENT, SESSION_INVALID_ERROR } from '../../utils/constants';
import { membershipCondition } from '../../utils/membership-condition';

type OrderProps = {
    membershipInfo: MemberOrderResponse;
};

export interface OrderData {
    subscriptionPlanId: string | undefined;
    supplementalDuesMemberType?: string;
    memberName?: string;
    memberId?: string;
    memberArchitectCount: number;
    nonMemberArchitectCount: number;
    associateCount: number;
    technicalStaffCount: number;
    otherStaffCount: number;
    additionalPackages?: object[];
    confirmLicense: boolean;
    confirmTerms: boolean;
    confirmInstallments: boolean;
    autoRenew: boolean;
}

const orderFormPropsAreEqual = (prev: OrderComponentProps, next: OrderComponentProps): boolean => {
    return prev.hasError === next.hasError && prev.isLoading === next.isLoading;
};

const MemoizedOrderComponent = memo(OrderComponent, orderFormPropsAreEqual);

const INSTALLMENTS_FAILURE_MODAL = {
    isOpen: true,
    title: 'Paying in installments failed',
    content: (
        <div>
            Thank you for attempting to renew your 2023 AIA Membership Dues. We are sorry that you were unable to
            complete your transaction online due to a timeout issue.
            <br />
            <br />
            Please contact the Support Center at (800) 242 3837, option 2, to complete your application for a Dues
            Installment Plan. We look forward to serving you during the hours of 8:30 am – 6:00 pm EST.
            <br />
            <br />
            Best regards,
            <br />
            The Membership Team
            <br />
            <a href="mailto:membersupport@aia.org">membersupport@aia.org</a>
        </div>
    ),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onCancel: (): void => {},
    cancelActionName: 'Close',
};

const redirectToCheckoutPage = async (paymentType: string, orderId: string, contactId?: string): Promise<void> => {
    const startUrl =
        paymentType === 'online'
            ? `${process.env.REACT_APP_CHECKOUT_PATH}/${orderId}`
            : `${process.env.REACT_APP_PDF_PATH}?recordId=${contactId}`;

    const redirectUrl = `${process.env.REACT_APP_SSO_URL}?startURL=${encodeURIComponent(startUrl)}`;
    window.location.assign(redirectUrl);
};

const scrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const useOrderForm = (
    membershipInfo: MemberOrderResponse,
): { formMethods: ReturnType<typeof useForm>; defaultSubscriptionPlanId: string | undefined } => {
    const membershipFlow = membershipCondition(
        membershipInfo.contact.membershipStatus,
        membershipInfo.contact.cesAudit,
        membershipInfo.contact.renewYear,
        membershipInfo.renewEligibleYears,
        membershipInfo.contact.membershipEpaymentStatus,
    );
    const isJoinFlow = membershipFlow === 'join' || membershipFlow === 'restart';
    const filteredSubscriptionPlans = membershipInfo.related?.subscriptionPlans?.filter((plan) => {
        const nameLower = plan.Name.toLowerCase();
        return isJoinFlow ? nameLower.includes('join') : nameLower.includes('renew');
    });
    const defaultSubscriptionPlanId = getDefaultSubscription(filteredSubscriptionPlans)?.Id;
    const schema = generateRenewSchema(
        !isJoinFlow && chargesSupplementalDues(membershipInfo.chapters, membershipInfo.contact?.emeritus),
    );
    const formOptions = generateRenewFormOptions({
        resolver: schema,
        defaultValues: { subscriptionPlanId: defaultSubscriptionPlanId || '' },
    });

    return { formMethods: useForm(formOptions), defaultSubscriptionPlanId };
};

const useErrorHandling = (): { handleError: (error: unknown, triedPayingWithInstallments: boolean) => void } => {
    const { setOptions: setModalOptions } = useContext(ModalContext);

    const handleError = (error: unknown, triedPayingWithInstallments: boolean): void => {
        if (triedPayingWithInstallments) {
            setModalOptions(INSTALLMENTS_FAILURE_MODAL);
        } else if (error === SESSION_INVALID_ERROR) {
            setModalOptions({ ...INVALID_SESSION_MODAL_CONTENT, isOpen: true });
        } else {
            setModalOptions({ ...DEFAULT_MODAL_CONTENT, isOpen: true });
        }
    };

    return { handleError };
};

const Order = ({ membershipInfo }: OrderProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');

    const { setOptions: setBackdropOptions } = useContext(BackdropContext);
    const { formMethods, defaultSubscriptionPlanId } = useOrderForm(membershipInfo);
    const { handleError } = useErrorHandling();

    const updateOrder = async (data: unknown, e: unknown): Promise<void> => {
        const request = createPayload(data as OrderData, membershipInfo, defaultSubscriptionPlanId);
        const submitAction = (e as { submitAction: string }).submitAction;

        try {
            setPaymentMethod(submitAction);
            setIsLoading(true);

            await sendPayload(submitAction, membershipInfo, request);

            sessionStorage.clear();
            setBackdropOptions?.({
                isOpen: true,
                child: <OrderRedirectToCheckout />,
                action: redirectToCheckoutPage(submitAction, request.OrderId, membershipInfo.contact?.id),
            });
        } catch (error) {
            setHasError(true);

            const triedPayingWithInstallments = !!membershipInfo.related?.subscriptionPlans?.find(
                (plan) => plan.Id === request.OrderApi__Subscription_Plan__c,
            )?.OrderApi__Number_Of_Installments__c;

            handleError(error, triedPayingWithInstallments);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormProvider {...formMethods}>
            <MemoizedOrderComponent
                membershipInfo={membershipInfo}
                isLoading={isLoading}
                hasError={hasError}
                paymentMethod={paymentMethod}
                onSubmit={formMethods.handleSubmit(updateOrder, scrollToTop)}
            />
        </FormProvider>
    );
};

export default Order;
